import React, { useMemo, useState } from 'react';
import { Radio, Button, Empty } from '@maxtropy/components';
import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { Divider, Spin } from 'antd';
import { useOverallTrend } from '../../hooks/useOverallTrend';
import { getBarChartOption } from './charts';
import { TrendGranularity } from '../../types';
import { isEmpty } from 'lodash-es';
import styles from './index.module.scss';
import {
  DEVICE_TYPE_MAP,
  GAS_PROPERTY_MAP,
  GasArray,
  GasPropertyKey,
  WATER_PROPERTY_MAP,
  WaterArray,
  WaterPropertyKey,
} from '../../constants';
import dayjs from 'dayjs';
import { formatThousandUnitDefaultTwo } from '@/utils/utils';
import { formatRequestTime } from '../../utils';
import qs from 'qs';

type OverallEmissionChartProp = {
  refreshKey: number;
  granularity: TrendGranularity;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
};

interface OverallEmissionChartProps extends OverallEmissionChartProp {
  title: string;
  deviceType: DEVICE_TYPE_MAP;
  defaultKey: number;
  color: string;
  refreshKey: number;
  granularity: TrendGranularity;
  dateRange: [any, any] | null;
}

export const timeFormat = (timeResolution: TrendGranularity) => {
  return timeResolution === TrendGranularity.HOUR
    ? 'MM-DD HH:mm'
    : timeResolution === TrendGranularity.DAY
    ? 'MM-DD'
    : 'YYYY-MM';
};

const OverallEmissionChart: React.FC<OverallEmissionChartProps> = ({
  title,
  deviceType,
  defaultKey,
  color,
  refreshKey,
  granularity,
  dateRange,
}) => {
  const theme = useTheme();
  const [activeKey, setActiveKey] = useState<number>(defaultKey);

  const indicatorMap = deviceType === DEVICE_TYPE_MAP.GAS ? GAS_PROPERTY_MAP : WATER_PROPERTY_MAP;

  const { data, loading } = useOverallTrend(deviceType, activeKey, granularity, dateRange, refreshKey);

  const chartOption = useMemo(() => {
    if (!data?.chartData?.length) return null;
    // 使用类型断言确保类型安全
    const label = indicatorMap[activeKey as keyof typeof indicatorMap];
    const formatTime = timeFormat(granularity);
    return getBarChartOption(data.chartData, color, label, formatTime, data.unit!);
  }, [data, color, indicatorMap, activeKey]);

  return (
    <div className={styles.container}>
      <Spin spinning={loading}>
        <Divider variant="dashed" style={{ borderColor: 'rgba(74,144,226,0.5)' }}>
          <div className={styles.title}>{title}</div>
        </Divider>
        <div className={styles.tabArea}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={activeKey}
            onChange={(e: any) => setActiveKey(e.target.value)}
          >
            {(deviceType === DEVICE_TYPE_MAP.GAS ? GasArray : WaterArray).map(item => (
              <Radio.Button key={item.propertyId} value={item.propertyId}>
                {item.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          <Button
            type="primary"
            onClick={() => {
              const { startTime, endTime } = formatRequestTime(dateRange!, granularity);

              window.open(
                `/api/v2/env/monitor/dashboard/export/overall-trend` +
                  qs.stringify(
                    {
                      deviceType,
                      propertyId: activeKey,
                      startTime,
                      endTime,
                      granularity,
                      timestamp: new Date().getTime(),
                    },
                    { addQueryPrefix: true }
                  )
              );
            }}
          >
            导出
          </Button>
        </div>
        <div className={styles.card}>
          <span className={styles.label}>{indicatorMap[activeKey as keyof typeof indicatorMap]}:</span>
          <span className={styles.value}>
            {formatThousandUnitDefaultTwo(data?.summaryValue)}
            {data?.unit}
          </span>
        </div>
        <div style={{ height: 320 }}>
          <EchartsWithTheme
            empty={{ isShowEmpty: isEmpty(chartOption), emptyNode: <Empty style={{ paddingTop: 80 }} /> }}
            option={chartOption}
            theme={theme}
            style={{ height: '100%' }}
          />
        </div>
      </Spin>
    </div>
  );
};

export const GasEmissionChart = (props: OverallEmissionChartProp) => (
  <OverallEmissionChart
    {...props}
    title="整体VOC废气监测"
    deviceType={DEVICE_TYPE_MAP.GAS}
    defaultKey={GasPropertyKey.noxEmission}
    color="#00ADFF"
  />
);

export const WaterEmissionChart = (props: OverallEmissionChartProp) => (
  <OverallEmissionChart
    {...props}
    title="整体废水监测"
    deviceType={DEVICE_TYPE_MAP.WATER}
    defaultKey={WaterPropertyKey.wastewaterVolume}
    color="#16DD8E"
  />
);
